import { createContext, useContext, useEffect, useState } from "react";
import GuestBadges from "components/GuestBadges";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { FaTimes, FaUser, FaHistory, FaStickyNote } from "react-icons/fa";
import { RiSparkling2Line } from "react-icons/ri";
import { useLocation, useNavigate } from "react-router-dom";
import { getGuestDetails, getGuestStays, resetGuestProfileView } from "store/slice/guestSlice";
import useCommon from "hooks/useCommon";
import usePermissions from "hooks/usePermissions";
import dayjs from "utils/dayjs";
import colors from "assets/theme/base/colors";
import VuiButton from "./VuiButton";

const GuestProfileContext = createContext(null);
export const useGuestProfileDrawer = () => useContext(GuestProfileContext);
const dateLabel = (value) =>
  value && dayjs(value).isValid() ? dayjs(value).format("MMM D, YYYY") : "Not available";
const summaryText = (value) =>
  typeof value === "string" ? value : value?.text || value?.summaryText || "";
const panel = {
  p: 2,
  borderRadius: "16px",
  border: "1px solid rgba(27,46,36,0.12)",
  bgcolor: "rgba(27,46,36,0.025)",
};

function Section({ title, icon, children }) {
  return (
    <Box sx={panel}>
      <Stack direction="row" alignItems="center" gap={1} mb={1.5}>
        {icon}
        <Typography component="h3" sx={{ fontSize: 14, fontWeight: 700 }}>
          {title}
        </Typography>
      </Stack>
      {children}
    </Box>
  );
}

export function GuestProfileName({ guestId, hotelId, name, guestFlag, guestFlagReason, sx }) {
  const drawer = useGuestProfileDrawer();
  const { can } = usePermissions();
  const enabled = Boolean(guestId && drawer && can("viewGuests"));
  return (
    <Typography
      component={enabled ? "button" : "span"}
      type={enabled ? "button" : undefined}
      onClick={
        enabled
          ? (event) => {
              event.stopPropagation();
              drawer.openGuestProfile({ guestId, hotelId, name, guestFlag, guestFlagReason });
            }
          : undefined
      }
      sx={{
        font: "inherit",
        fontWeight: 400,
        color: "inherit",
        textAlign: "left",
        p: 0,
        border: 0,
        background: "none",
        ...(enabled && {
          cursor: "pointer",
          "&:hover": { textDecoration: "underline" },
          "&:focus-visible": { outline: "2px solid #47856b", outlineOffset: 3 },
        }),
        ...sx,
      }}
    >
      {name || "Guest"}
    </Typography>
  );
}

export function GuestProfileProvider({ children }) {
  const { STORE, DISPATCH } = useCommon();
  const selectedHotelIds = STORE((state) => state.ui.hotelIds);
  const { can } = usePermissions();
  const permitted = can("viewGuests");
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [selection, setSelection] = useState(null);
  const profile = STORE((state) => state.guests.profileViews.drawer);
  const details = profile?.details?.data;
  const stays = profile?.stays?.data?.stays || [];
  const loading = profile?.details?.loading || false;
  const staysLoading = profile?.stays?.loading || false;
  const error =
    profile?.details?.error || (details && !details.guest ? "Guest profile unavailable" : null);
  const staysError = profile?.stays?.error;
  const [retry, setRetry] = useState(0);
  const scope = JSON.stringify(selection?.hotelId ? [selection.hotelId] : selectedHotelIds || []);

  useEffect(() => {
    setSelection(null);
  }, [pathname, permitted]);
  useEffect(() => {
    if (!selection || !permitted) return;
    const args = { guestId: selection.guestId, hotelIds: JSON.parse(scope), profileView: "drawer" };
    const detailsRequest = DISPATCH(getGuestDetails(args));
    const staysRequest = DISPATCH(getGuestStays({ ...args, page: 1, pageSize: 5 }));
    return () => {
      detailsRequest.abort();
      staysRequest.abort();
      DISPATCH(resetGuestProfileView("drawer"));
    };
  }, [DISPATCH, selection, scope, permitted, retry]);

  const openGuestProfile = (guest) => {
    if (!guest?.guestId || !permitted) return;
    DISPATCH(resetGuestProfileView("drawer"));
    setSelection(guest);
  };
  const close = () => setSelection(null);
  const guest = details?.guest;
  const counters = details?.counters || {};
  const notes = Array.isArray(details?.profileNotes) ? details.profileNotes : [];
  const summary = summaryText(guest?.latestStayAiSummary);
  const name = guest?.name || selection?.name || "Guest profile";

  return (
    <GuestProfileContext.Provider value={{ openGuestProfile, closeGuestProfile: close }}>
      {children}
      <Drawer
        anchor="right"
        open={Boolean(selection && permitted)}
        onClose={close}
        PaperProps={{
          role: "dialog",
          "aria-modal": true,
          "aria-labelledby": "guest-profile-title",
          sx: {
            width: { xs: "100%", sm: 510 },
            maxWidth: "100vw",
            height: "100%",
            m: 0,
            borderRadius: 0,
            bgcolor: colors.light.main,
            color: colors.dark.main,
            backgroundImage: "none",
          },
        }}
      >
        <Box
          sx={{
            p: 2,
            borderBottom: "1px solid rgba(27,46,36,0.12)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box>
            <Typography
              sx={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 2, opacity: 0.65 }}
            >
              Guest intelligence
            </Typography>
            <Typography
              id="guest-profile-title"
              component="h2"
              sx={{ fontSize: 20, fontWeight: 700 }}
            >
              Profile overview
            </Typography>
          </Box>
          <IconButton onClick={close} aria-label="Close guest profile">
            <FaTimes size={17} />
          </IconButton>
        </Box>
        <Box sx={{ flex: 1, overflowY: "auto", p: 2.5 }}>
          {loading ? (
            <Stack spacing={2} role="status" aria-label="Loading guest profile">
              {[100, 80, 160, 120].map((height, index) => (
                <Skeleton
                  key={index}
                  variant="rectangular"
                  height={height}
                  sx={{ borderRadius: 2 }}
                />
              ))}
            </Stack>
          ) : error ? (
            <Alert
              severity="error"
              action={<Button onClick={() => setRetry((value) => value + 1)}>Retry</Button>}
            >
              {error}
            </Alert>
          ) : (
            guest && (
              <Stack spacing={2}>
                <Box sx={{ ...panel, bgcolor: "transparent" }}>
                  <Stack direction="row" alignItems="center" gap={1.5}>
                    <Avatar
                      sx={{
                        width: 58,
                        height: 58,
                        flexShrink: 0,
                        bgcolor: colors.light.secondary,
                        border: "1px solid rgba(27,46,36,0.12)",
                        color: colors.dark.main,
                        fontSize: 20,
                      }}
                    >
                      {name
                        .split(/\s+/)
                        .slice(0, 2)
                        .map((part) => part[0])
                        .join("")}
                    </Avatar>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        component="h3"
                        sx={{ fontSize: 22, fontWeight: 700, whiteSpace: "normal", overflowWrap: "anywhere", lineHeight: 1.3 }}
                      >
                        {name}
                      </Typography>
                      <GuestBadges badges={guest.badges ?? details?.badges} />
                      <Typography sx={{ fontSize: 12, opacity: 0.7 }}>
                        {counters.totalStays == null
                          ? "Guest profile"
                          : `${counters.totalStays} recorded stays`}
                      </Typography>
                    </Box>
                  </Stack>
                  <Divider sx={{ my: 2 }} />
                  <Box display="grid" gridTemplateColumns="repeat(2, minmax(0, 1fr))" gap={2}>
                    {[
                      ["Email", guest.email],
                      ["Phone", guest.phone],
                      ["Nationality", guest.nationality],
                      ["Language", guest.language],
                    ].map(([label, value]) => (
                      <Box key={label} minWidth={0}>
                        <Typography sx={{ fontSize: 10, opacity: 0.65 }}>{label}</Typography>
                        <Typography sx={{ fontSize: 12, whiteSpace: "normal", overflowWrap: "anywhere" }}>
                          {value || "Not provided"}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
                <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1}>
                  {[
                    ["Total stays", counters.totalStays ?? "—"],
                    [
                      "Average stay",
                      counters.avgStayDurationDays != null
                        ? `${counters.avgStayDurationDays} days`
                        : "—",
                    ],
                    ["First arrival", dateLabel(counters.firstStayCheckInDate)],
                    ["Latest departure", dateLabel(counters.mostRecentStayCheckOutDate)],
                  ].map(([label, value]) => (
                    <Box key={label} sx={panel}>
                      <Typography sx={{ fontSize: 10, opacity: 0.65 }}>{label}</Typography>
                      <Typography sx={{ fontSize: 14, fontWeight: 600, mt: 0.5 }}>
                        {value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                <Section title="AI summary" icon={<RiSparkling2Line size={17} />}>
                  <Typography
                    sx={{
                      fontSize: 13,
                      lineHeight: 1.8,
                      whiteSpace: "pre-wrap",
                      overflowWrap: "anywhere",
                    }}
                  >
                    {summary || "No AI summary available yet."}
                  </Typography>
                </Section>
                <Section title="Profile note" icon={<FaStickyNote size={14} />}>
                  {notes.length ? (
                    notes.map((note, index) => (
                      <Box key={note.noteId || index} sx={{ mt: index ? 1.5 : 0 }}>
                        <Typography
                          sx={{
                            fontSize: 13,
                            lineHeight: 1.7,
                            whiteSpace: "pre-wrap",
                            overflowWrap: "anywhere",
                          }}
                        >
                          {note.text}
                        </Typography>
                        <Typography sx={{ fontSize: 10, opacity: 0.65, mt: 0.5 }}>
                          {[note.authorName, note.createdAt ? dateLabel(note.createdAt) : null]
                            .filter(Boolean)
                            .join(" · ")}
                        </Typography>
                      </Box>
                    ))
                  ) : (
                    <Typography sx={{ fontSize: 12, opacity: 0.65 }}>
                      No profile note recorded.
                    </Typography>
                  )}
                </Section>
                <Section title="Recent stays" icon={<FaHistory size={14} />}>
                  {staysLoading ? (
                    <Skeleton height={90} />
                  ) : staysError ? (
                    <Alert
                      severity="warning"
                      action={<Button onClick={() => setRetry((value) => value + 1)}>Retry</Button>}
                    >
                      Stay history could not be loaded.
                    </Alert>
                  ) : stays.length ? (
                    stays.map((stay, index) => (
                      <Box
                        key={stay.reservationId || index}
                        sx={{ py: 1.2, borderBottom: "1px solid rgba(27,46,36,0.1)" }}
                      >
                        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                          {stay.hotelName || "Property"}
                        </Typography>
                        <Typography sx={{ fontSize: 11, mt: 0.5 }}>
                          Room {stay.roomName || "not provided"}
                        </Typography>
                        <Typography sx={{ fontSize: 11, opacity: 0.7 }}>
                          {dateLabel(stay.stay?.checkInDate)} → {dateLabel(stay.stay?.checkOutDate)}
                        </Typography>
                      </Box>
                    ))
                  ) : (
                    <Typography sx={{ fontSize: 12, opacity: 0.65 }}>No stays recorded.</Typography>
                  )}
                </Section>
              </Stack>
            )
          )}
        </Box>
        {guest && !loading && !error && (
          <Box sx={{ p: 2.5, borderTop: "1px solid rgba(27,46,36,0.12)" }}>
            <VuiButton
              fullWidth
              variant="contained"
              color="primary"
              startIcon={<FaUser size={12} />}
              onClick={() => {
                navigate(`/guestLibrary/profile-history/${selection.guestId}`);
                close();
              }}
            >
              Open full profile & history
            </VuiButton>
          </Box>
        )}
      </Drawer>
    </GuestProfileContext.Provider>
  );
}
